import axios from 'axios'

// Base URL for the API - can be configured through environment variables
const API_BASE_URL =
  process.env.REACT_APP_API_BASE_URL || 'http://localhost:5500/api'

// Create an axios instance with common settings
const apiClient = axios.create({
  baseURL: API_BASE_URL,
  headers: {
    'Content-Type': 'application/json'
  }
})

// Add authentication handling if needed
apiClient.interceptors.request.use(
  config => {
    const token = localStorage.getItem('token')
    if (token) {
      config.headers.Authorization = `Bearer ${token}`
    }
    return config
  },
  error => Promise.reject(error)
)

// Message service functions
const messageService = {
  // Get all messages for a user by email
  getAllMessagesByEmail: async email => {
    try {
      const response = await apiClient.get(`/messages/email/${email}`)
      return response.data
    } catch (error) {
      console.error('Error fetching messages:', error)
      throw error
    }
  },

  // Get message summary for a user by email
  getMessageSummaryByEmail: async email => {
    try {
      const response = await apiClient.get(`/messages/summary/email/${email}`)
      return response.data
    } catch (error) {
      console.error('Error fetching message summary:', error)
      throw error
    }
  },

  // Get message history with pagination and filtering
  getMessageHistoryByEmail: async (email, params = {}) => {
    try {
      const response = await apiClient.get(`/messages/history/email/${email}`, {
        params
      })
      return response.data
    } catch (error) {
      console.error('Error fetching message history:', error)
      throw error
    }
  },

  // Get messages for a specific conversation
  getMessagesByConversation: async (email, conversationId) => {
    try {
      const params = { conversationId } // Fixed typo: was converstaionId
      const response = await apiClient.get(`/messages/history/email/${email}`, {
        params
      })
      return response.data
    } catch (error) {
      console.error('Error fetching conversation messages:', error)
      throw error
    }
  },
  // Send a new message
  sendMessage: async data => {
    try {
      const response = await apiClient.post('/messages/send', data)
      return response.data
    } catch (error) {
      console.error('Error sending message:', error)
      throw error
    }
  },

  // Mark messages as read
  markAsRead: async messageIds => {
    try {
      const response = await apiClient.put('/messages/read', { messageIds })
      return response.data
    } catch (error) {
      console.error('Error marking messages as read:', error)
      throw error
    }
  },

  // Get all messages grouped by user email with pagination
  getAllMessagesGroupedByEmail: async (params = {}) => {
    try {
      const response = await apiClient.get('/messages/grouped/email', {
        params
      })
      return response.data
    } catch (error) {
      console.error('Error fetching grouped messages:', error)
      return {
        error: true,
        message: 'Failed to fetch messages. Please try again.'
      }
    }
  },

  // Get detailed messages for a specific email
  getDetailedMessagesByEmail: async (email, params = {}) => {
    try {
      const response = await apiClient.get(`/messages/email/${email}/details`, {
        params
      })
      return response.data
    } catch (error) {
      console.error('Error fetching detailed messages:', error)
      return {
        error: true,
        message: 'Failed to fetch message details. Please try again.'
      }
    }
  },

  // Search messages for a specific email
  // searchMessagesByEmail: async (email, searchTerm, params = {}) => {
  //     try {
  //         const searchParams = { ...params, search: searchTerm };
  //         const response = await apiClient.get(`/messages/email/${email}/details`, {
  //             params: searchParams
  //         });
  //         return response.data;
  //     } catch (error) {
  //         console.error('Error searching messages:', error);
  //         return { error: true, message: 'Failed to search messages. Please try again.' };
  //     }
  // },

  searchMessagesByEmail: async (email, searchTerm, params = {}) => {
    if (!email) {
      console.error('Email is required for searching messages')
      return {
        error: true,
        message: 'Email is required for searching messages',
        data: []
      }
    }

    try {
      // Combine the search term with existing params
      const searchParams = {
        ...params,
        search: searchTerm
      }

      // Log the request parameters for debugging
      console.log(
        'Searching messages for email:',
        email,
        'with params:',
        searchParams
      )

      const response = await apiClient.get(`/messages/email/${email}/details`, {
        params: searchParams
      })

      // Return the data even if empty
      return {
        ...response.data,
        data: response.data.data || [] // Ensure data is always an array
      }
    } catch (error) {
      console.error('Error searching messages:', error)

      // Return a structured error response with empty data array
      return {
        error: true,
        message: 'Failed to search messages. Please try again.',
        data: []
      }
    }
  },

  // // Get message statistics grouped by email
  getMessageStatsByEmail: async () => {
      try {
          const response = await apiClient.get('/messages/stats/email');
          return response.data;
      } catch (error) {
          console.error('Error fetching message statistics:', error);
          return { error: true, message: 'Failed to fetch message statistics. Please try again.' };
      }
  }
}

export default messageService
